'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Database, RefreshCw } from 'lucide-react';
import {
  getRatelimitMetrics,
  getWorkloadMetrics,
  type MetricsStorageUsage as StorageUsage,
} from '@/lib/urm-api';
import { cn } from '@/lib/utils';

type MetricsKind = 'workload' | 'rate-limit';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes.toLocaleString()} B`;

  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[unitIndex]}`;
}

async function fetchStorage(kind: MetricsKind): Promise<StorageUsage> {
  const response = kind === 'workload'
    ? await getWorkloadMetrics({ limit: 1, includeStorage: true })
    : await getRatelimitMetrics({ limit: 1, includeStorage: true });

  if (!response.storage) {
    throw new Error('Storage usage was not returned by the server');
  }
  return response.storage;
}

export function MetricsStorageUsage({ kind }: { kind: MetricsKind }) {
  const [storage, setStorage] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const initialRequestRef = useRef<{
    kind: MetricsKind;
    promise: Promise<StorageUsage>;
  } | null>(null);
  const label = kind === 'workload' ? 'workload' : 'rate limit';

  const settleRequest = useCallback((request: Promise<StorageUsage>, requestId: number) => {
    void request.then((nextStorage) => {
      if (requestIdRef.current !== requestId) return;
      setStorage(nextStorage);
      setError(null);
    }).catch((caught: unknown) => {
      if (requestIdRef.current !== requestId) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    }).finally(() => {
      if (requestIdRef.current === requestId) setLoading(false);
    });
  }, []);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (initialRequestRef.current?.kind !== kind) {
      initialRequestRef.current = { kind, promise: fetchStorage(kind) };
    }
    settleRequest(initialRequestRef.current.promise, requestId);

    return () => {
      requestIdRef.current += 1;
    };
  }, [kind, settleRequest]);

  const refresh = () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    settleRequest(fetchStorage(kind), requestId);
  };

  const measuredAt = storage?.measured_at_ms
    ? new Date(storage.measured_at_ms).toLocaleString()
    : null;
  const title = error
    ?? (measuredAt ? `Measured ${measuredAt}${storage?.approximate ? ' (approximate)' : ''}` : undefined);
  return (
    <div
      aria-label={`${label} storage usage`}
      className="inline-flex h-6 max-w-full items-center gap-1 rounded-md border bg-background px-1.5 text-[11px] text-muted-foreground"
      title={title}
    >
      <Database className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="whitespace-nowrap" aria-live="polite">
        {storage
          ? `Storage: ${storage.approximate ? '≈' : ''}${formatBytes(storage.redis_memory_bytes)}`
          : loading ? 'Storage…' : 'Storage unavailable'}
      </span>
      <button
        type="button"
        aria-label={`Refresh ${label} storage usage`}
        title={`Refresh ${label} storage usage`}
        disabled={loading}
        onClick={refresh}
        className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
      >
        <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} aria-hidden="true" />
      </button>
    </div>
  );
}
